import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { MediaFile, MediaImage } from '../../models/media';
import { updateFile, updateImage } from '../../services/media';

interface FileDetailsProps {
  file: MediaFile | MediaImage;
  open: boolean;
  onClose: () => void;
  onUpdate: (file: MediaFile | MediaImage) => void;
}

const FileDetails: React.FC<FileDetailsProps> = ({ file, open, onClose, onUpdate }) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [name, setName] = useState<string>(file.name);
  const [description, setDescription] = useState<string>(file.description || '');
  const [altText, setAltText] = useState<string>(
    'default_alt_text' in file ? file.default_alt_text || '' : ''
  );
  const [caption, setCaption] = useState<string>(
    'default_caption' in file ? file.default_caption || '' : ''
  );
  
  const isImage = 'is_image' in file && file.is_image;
  const thumbnailUrl = isImage 
    ? (file as MediaImage).thumbnail_url?.large || (file as MediaImage).thumbnail_url?.original
    : null;
  
  const handleSave = async () => {
    try {
      let updatedFile;
      
      if (isImage) {
        updatedFile = await updateImage(file.id, {
          name,
          description,
          alt_text: altText,
          caption,
        });
      } else {
        updatedFile = await updateFile(file.id, {
          name,
          description,
        });
      }
      
      onUpdate(updatedFile);
      setEditing(false);
    } catch (error) {
      console.error('Error updating file:', error);
    }
  };
  
  const handleCancel = () => {
    setName(file.name);
    setDescription(file.description || '');
    if (isImage) {
      setAltText('default_alt_text' in file ? file.default_alt_text || '' : '');
      setCaption('default_caption' in file ? file.default_caption || '' : '');
    }
    setEditing(false);
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  
  const OpenExternalButton = () => (
    <IconButton 
      size="small"
      component="a"
      href={file.file}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in new tab"
    >
      <OpenInNewIcon fontSize="small" />
    </IconButton>
  );
  
  return (
    <Dialog 
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: 800,
        }
      }}
    >
      <DialogTitle>
        File Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Preview Section */}
          <Grid item xs={12} md={6}>
            <Paper 
              variant="outlined" 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 300,
              }}
            >
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">Preview</Typography>
                <OpenExternalButton />
              </Box>
              <Divider />
              <Box 
                sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  bgcolor: 'background.default',
                  overflow: 'auto',
                  p: 2
                }}
              >
                {isImage && thumbnailUrl ? (
                  <img 
                    src={thumbnailUrl} 
                    alt={name}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <Box 
                    sx={{ 
                      p: 4, 
                      borderRadius: 1, 
                      border: '1px dashed',
                      borderColor: 'divider',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No preview available for this file type
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      sx={{ mt: 2 }}
                      href={file.file}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download File
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* Details Section */}
          <Grid item xs={12} md={6}>
            <Paper 
              variant="outlined" 
              sx={{ height: '100%', minHeight: 300 }}
            >
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">Details</Typography>
                {!editing ? (
                  <IconButton size="small" onClick={() => setEditing(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Box>
                    <IconButton size="small" color="primary" onClick={handleSave}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleCancel}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {editing ? (
                      <TextField
                        label="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <>
                        <Typography variant="caption" color="textSecondary">Name</Typography>
                        <Typography variant="body1">{file.name}</Typography>
                      </>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    {editing ? (
                      <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                      />
                    ) : (
                      <>
                        <Typography variant="caption" color="textSecondary">Description</Typography>
                        <Typography variant="body1">
                          {file.description || <em>No description</em>}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  
                  {isImage && (
                    <>
                      <Grid item xs={12}>
                        {editing ? (
                          <TextField
                            label="Alt Text"
                            value={altText}
                            onChange={(e) => setAltText(e.target.value)}
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Alternative text for accessibility"
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="textSecondary">Alt Text</Typography>
                            <Typography variant="body1">
                              {(file as MediaImage).default_alt_text || <em>No alt text</em>}
                            </Typography>
                          </>
                        )}
                      </Grid>
                      
                      <Grid item xs={12}>
                        {editing ? (
                          <TextField
                            label="Caption"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            fullWidth
                            variant="outlined"
                            size="small"
                            multiline
                            rows={2}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="textSecondary">Caption</Typography>
                            <Typography variant="body1">
                              {(file as MediaImage).default_caption || <em>No caption</em>}
                            </Typography>
                          </>
                        )}
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">File Type</Typography>
                    <Typography variant="body2">{file.file_type.toUpperCase()}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Size</Typography>
                    <Typography variant="body2">{file.file_size_display}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Original Filename</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {file.original_filename}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Uploaded By</Typography>
                    <Typography variant="body2">{file.owner_name || `User ID: ${file.owner}`}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Uploaded On</Typography>
                    <Typography variant="body2">{formatDate(file.uploaded_at)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Last Modified</Typography>
                    <Typography variant="body2">{formatDate(file.modified_at)}</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default FileDetails;
